import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { User, Calendar, Activity } from "lucide-react";
import type { UserProfile, Subscription } from "@/services/api";
import { fetchPostDumpsStatus, fetchSubscription } from "@/services/api";
import { cn } from "@/lib/utils";
import { useState, useEffect } from "react";
import { getCurrentPlan } from "./SubscriptionContent";

interface ProfileContentProps {
  userInfo: Omit<UserProfile, 'premium_until'> & {
    premiumUntil: string | null;
  };
  reload: boolean;
}

// Plan configuration interface
interface PlanConfig {
  name: string;
  description: string;
  icon: React.ReactNode;
  bgColor: string;
  borderColor: string;
  textColor: string;
  badgeColor: string;
  amount: string | null;
  subscriptionStatus: string | null;
  validity: string | null;
}



export function ProfileContent({ userInfo, reload }: ProfileContentProps) {
  const joinDate = new Date(userInfo.created_at || Date.now()).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // State for subscription data
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(true);

  // Calculate premium status based on subscription and premiumUntil
  const isPremium = subscription?.status === 'active' || (userInfo.premiumUntil && new Date(userInfo.premiumUntil) > new Date());

  // Get current plan details
  const currentPlan = getCurrentPlan(subscription, userInfo.premiumUntil);

  // State for post dumps status
  const [postDumpsStatus, setPostDumpsStatus] = useState<{
    unprocessed_post_dumps_count: number;
    can_process: boolean;
    current_posts_count?: number;
    max_posts_limit: number;
  } | null>(null);
  const [isLoadingStats, setIsLoadingStats] = useState(true);

  // Fetch subscription data
  useEffect(() => {
    const loadSubscription = async () => {
      try {
        setIsLoadingSubscription(true);
        const subscriptionData = await fetchSubscription();
        setSubscription(subscriptionData);
      } catch (error) {
        console.error('Failed to load subscription:', error);
        setSubscription(null);
      } finally {
        setIsLoadingSubscription(false);
      }
    };

    loadSubscription();
  }, [reload]);

  // Fetch post dumps status
  useEffect(() => {
    const loadPostDumpsStatus = async () => {
      try {
        setIsLoadingStats(true);
        const status = await fetchPostDumpsStatus();
        setPostDumpsStatus(status);
      } catch (error) {
        console.error('Failed to load post dumps status:', error);
        // Set default values on error
        setPostDumpsStatus({
          unprocessed_post_dumps_count: 0,
          can_process: false,
          current_posts_count: 0,
          max_posts_limit: 0
        });
      } finally {
        setIsLoadingStats(false);
      }
    };

    loadPostDumpsStatus();
  }, [reload]);

  // Calculate remaining limit
  const remainingLimit = postDumpsStatus 
    ? Math.max(0, postDumpsStatus.max_posts_limit - (postDumpsStatus.current_posts_count || 0))
    : 0;

  return (
    <div className="space-y-8 w-full">
      {/* Header Section */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">Profile Settings</h1>
        <p className="text-gray-400">Manage your account information and preferences</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Profile Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Profile Card */}
          <Card className="bg-gray-800/50 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <User className="w-5 h-5" />
                Personal Information
              </CardTitle>
              <CardDescription className="text-gray-400">
                Your basic profile details
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-sm font-medium text-gray-300 mb-2 block">Profile Name</label>
                  <Input
                    type="text"
                    value={userInfo.name ?? userInfo.email.split('@')[0]}
                    className="bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                    readOnly
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-300 mb-2 block">Email Address</label>
                  <Input
                    type="email"
                    value={userInfo.email}
                    className="bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                    readOnly
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-sm font-medium text-gray-300 mb-2 block">Account Status</label>
                  <div className="flex items-center gap-2">
                    {isLoadingSubscription ? (
                      <div className="w-16 h-6 bg-gray-700 rounded animate-pulse"></div>
                    ) : (
                      <>
                        <Badge variant={isPremium ? "default" : "secondary"} className={isPremium ? "bg-purple-600" : "bg-gray-600"}>
                          {currentPlan.name}
                        </Badge>
                        {isPremium && (
                          <span className="text-sm text-gray-400">
                            Until {new Date(userInfo.premiumUntil!).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-300 mb-2 block">Member Since</label>
                  <div className="flex items-center gap-2 text-gray-400">
                    <Calendar className="w-4 h-4" />
                    <span>{joinDate}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Account Statistics */}
          <Card className="bg-gray-800/50 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Activity className="w-5 h-5" />
                Account Statistics
              </CardTitle>
              <CardDescription className="text-gray-400">
                Your usage and activity overview
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingStats ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center p-4 bg-gray-700/50 rounded-lg">
                    <div className="text-2xl font-bold text-white mb-1">...</div>
                    <div className="text-sm text-gray-400">Posts Synced</div>
                  </div>
                  <div className="text-center p-4 bg-gray-700/50 rounded-lg">
                    <div className="text-2xl font-bold text-white mb-1">...</div>
                    <div className="text-sm text-gray-400">Unprocessed Posts</div>
                  </div>
                  <div className="text-center p-4 bg-gray-700/50 rounded-lg">
                    <div className="text-2xl font-bold text-white mb-1">...</div>
                    <div className="text-sm text-gray-400">Remaining Limit</div>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center p-4 bg-gray-700/50 rounded-lg">
                    <div className="text-2xl font-bold text-white mb-1">
                      {postDumpsStatus?.current_posts_count || 0}
                    </div>
                    <div className="text-sm text-gray-400">Posts Synced</div>
                  </div>
                  <div className="text-center p-4 bg-gray-700/50 rounded-lg">
                    <div className="text-2xl font-bold text-white mb-1">
                      {postDumpsStatus?.unprocessed_post_dumps_count || 0}
                    </div>
                    <div className="text-sm text-gray-400">Unprocessed Posts</div>
                  </div>
                  <div className="text-center p-4 bg-gray-700/50 rounded-lg">
                    <div className="text-2xl font-bold text-white mb-1">
                      {remainingLimit}
                    </div>
                    <div className="text-sm text-gray-400">Remaining Limit</div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>


        </div>

        {/* Sidebar */}
        <div className="space-y-6">

          {/* Account Status */}
          <Card className="bg-gray-800/50 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white text-lg">Account Status</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {isLoadingSubscription ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-300">Plan</span>
                    <div className="w-16 h-6 bg-gray-700 rounded animate-pulse"></div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-300">Status</span>
                    <div className="w-20 h-4 bg-gray-700 rounded animate-pulse"></div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-300">Plan</span>
                    <Badge variant={isPremium ? "default" : "secondary"} className={isPremium ? "bg-purple-600" : "bg-gray-600"}>
                      {currentPlan.name}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-300">Status</span>
                    <span className={cn(
                      "text-sm",
                      currentPlan.subscriptionStatus === 'ACTIVE' ? "text-green-400" : 
                      currentPlan.subscriptionStatus === 'EXPIRED' ? "text-red-400" : 
                      "text-gray-400"
                    )}>
                      {currentPlan.subscriptionStatus || 'Active'}
                    </span>
                  </div>
                  {currentPlan.validity && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-300">
                        {currentPlan.subscriptionStatus === 'ACTIVE' ? 'Valid Until' : 'Expired On'}
                      </span>
                      <span className="text-gray-400 text-sm">
                        {currentPlan.validity}
                      </span>
                    </div>
                  )}
                  {currentPlan.amount && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-300">Amount</span>
                      <span className="text-gray-400 text-sm">
                        {currentPlan.amount}
                      </span>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Support */}
          <Card className="bg-gray-800/50 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white text-lg">Need Help?</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-400 text-sm mb-4">
                Contact our support team for assistance with your account or mail to us at <span className="text-white">hello@instavault.co</span>
              </p>
              <Button
                variant="outline"
                className="w-full bg-gray-700 border-gray-600 text-white hover:bg-purple-600 hover:text-white"
                onClick={() => window.open("mailto:hello@instavault.co")}
              >
                Contact Support
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
} 